import { themeVars } from "@/theme/theme.css";
import { Button } from "@/ui/button";
import { ScrollArea } from "@/ui/scroll-area";
import { Title } from "@/ui/typography";
import type { CSSProperties } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";

export default function ErrorBoundary() {
	const error = useRouteError();
	const navigate = useNavigate();

	const err = error as { message?: string } | null | undefined;
	const errorMessage = typeof err?.message === "string" ? err.message : String(error ?? "");
	const isChunkError =
		errorMessage.includes("Failed to fetch dynamically imported module") ||
		errorMessage.includes("error loading dynamically imported module") ||
		errorMessage.includes("dynamically imported module") ||
		/Loading( chunk \d+)? failed/.test(errorMessage) ||
		errorMessage.includes("Loading chunk") ||
		(error instanceof Error && error.name === "ChunkLoadError") ||
		errorMessage.includes("MIME type") ||
		errorMessage.includes("MIME-típusa") ||
		(errorMessage.includes("blocked") && errorMessage.includes("MIME"));

	const handleRetry = () => {
		// Clear cache and reload
		if (typeof window !== "undefined") {
			// Clear all caches
			if ("caches" in window) {
				caches.keys().then((names) => {
					names.forEach((name) => caches.delete(name));
				});
			}
			// Reload the page
			window.location.reload();
		}
	};

	return (
		<ScrollArea className="w-full h-screen">
			<div style={rootStyles()}>
				<div style={containerStyles()}>
					{isChunkError ? (
						<>
							<Title as="h2">Az oldal verziója frissült</Title>
							<p style={hintStyles()}>
								Kérjük, frissítsd az oldalt a legújabb funkciók eléréséhez.
							</p>
							<div style={retryButtonStyles()}>
								<Button onClick={handleRetry} variant="default">
									Oldal újratöltése
								</Button>
								<Button onClick={() => navigate(-1)} variant="outline" style={{ marginLeft: 12 }}>
									Vissza
								</Button>
							</div>
						</>
					) : (
						renderErrorMessage(error)
					)}
				</div>
			</div>
		</ScrollArea>
	);
}

function parseStackTrace(stack?: string) {
	if (!stack) return { filePath: null, functionName: null };

	const filePathMatch = stack.match(/\/src\/[^?]+/);
	const functionNameMatch = stack.match(/at (\S+)/);

	return {
		filePath: filePathMatch ? filePathMatch[0] : null,
		functionName: functionNameMatch ? functionNameMatch[1] : null,
	};
}

function renderErrorMessage(error: any) {
	if (isRouteErrorResponse(error)) {
		return (
			<>
				<Title as="h2">
					{error.status}: {error.statusText}
				</Title>
				<p style={messageStyles()}>{error.data}</p>
			</>
		);
	}

	if (error instanceof Error) {
		const { filePath, functionName } = parseStackTrace(error.stack);

		return (
			<>
				<Title as="h2">Unexpected Application Error!</Title>
				<p style={messageStyles()}>
					{error.name}: {error.message}
				</p>
				<pre style={detailsStyles()}>{error.stack}</pre>
				{(filePath || functionName) && (
					<p style={filePathStyles()}>
						{filePath} ({functionName})
					</p>
				)}
			</>
		);
	}

	return <Title as="h2">Unknown Error</Title>;
}

const rootStyles = (): CSSProperties => {
	return {
		display: "flex",
		height: "100vh",
		flex: "1 1 auto",
		alignItems: "center",
		padding: "10vh 15px",
		flexDirection: "column",
		color: "white",
		backgroundColor: "#2c2c2e",
	};
};

const containerStyles = (): CSSProperties => {
	return {
		gap: 24,
		padding: 20,
		width: "100%",
		maxWidth: 960,
		display: "flex",
		borderRadius: 8,
		flexDirection: "column",
		backgroundColor: "#1c1c1e",
	};
};

const messageStyles = (): CSSProperties => {
	return {
		margin: 0,
		lineHeight: 1.5,
		padding: "12px 16px",
		whiteSpace: "pre-wrap",
		color: themeVars.colors.palette.error.default as string,
		backgroundColor: "#2a1e1e",
		borderLeft: `2px solid ${themeVars.colors.palette.error.default as string}`,
		fontWeight: 700,
	};
};

const detailsStyles = (): CSSProperties => {
	return {
		margin: 0,
		padding: 16,
		lineHeight: 1.5,
		overflow: "auto",
		borderRadius: "inherit",
		color: themeVars.colors.palette.warning.default as string,
		whiteSpace: "pre-wrap",
		backgroundColor: "#111111",
	};
};

const filePathStyles = (): CSSProperties => {
	return {
		marginTop: 16,
		color: themeVars.colors.palette.info.default as string,
	};
};

const hintStyles = (): CSSProperties => {
	return {
		marginTop: 8,
		marginBottom: 0,
		color: "rgba(255,255,255,0.85)",
		fontSize: "1rem",
	};
};

const retryButtonStyles = (): CSSProperties => {
	return {
		marginTop: 24,
		display: "flex",
		gap: 12,
		justifyContent: "center",
	};
};
